import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { SERVICES } from '../../data/mock';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname === path;

  const handleServiceClick = (slug) => {
    setServicesOpen(false);
    setMobileMenuOpen(false);
    navigate(`/services/${slug}`);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 backdrop-blur-xl shadow-2xl" style={{
      background: 'linear-gradient(to right, rgba(76, 51, 98, 0.95) 0%, rgba(76, 51, 98, 0.85) 30%, rgba(76, 51, 98, 0.75) 60%, rgba(76, 51, 98, 0.65) 100%)'
    }}>
      <div className="container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <div className="transition-all hover:scale-105">
              <img
                src="/logo.png"
                alt="Great Engineers Logo"
                className="h-16 w-16 object-contain drop-shadow-2xl"
              />
            </div>
            <span className="text-white font-bold text-2xl tracking-tight drop-shadow-lg">
              Great Engineers
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-bold transition-colors hover:text-teal-300 ${isActive('/') ? 'text-teal-300' : 'text-white/90'
                }`}
            >
              Home
            </Link>

            {/* Services Dropdown */}
            <div className="relative">
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className={`flex items-center gap-1 text-sm font-bold transition-colors hover:text-teal-300 ${location.pathname.includes('/services') ? 'text-teal-300' : 'text-white/90'
                  }`}
              >
                Services
                <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>

              {servicesOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setServicesOpen(false)}
                  />
                  <div className="absolute top-full left-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-xl py-2 max-h-[70vh] overflow-y-auto z-50">
                    <button
                      onClick={() => { setServicesOpen(false); navigate('/services'); }}
                      className="w-full text-left px-4 py-3 text-sm font-semibold text-[#4C3362] hover:bg-purple-50 transition-colors flex items-center gap-2"
                    >
                      <span className="w-2 h-2 rounded-full bg-[#4C3362]"></span>
                      View All Services
                    </button>
                    <div className="border-t border-slate-100 my-2" />
                    {SERVICES.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => handleServiceClick(service.slug)}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors flex items-center gap-3"
                      >
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: service.color }}
                        />
                        {service.title}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <Link
              to="/case-studies"
              className={`text-sm font-bold transition-colors hover:text-teal-300 ${isActive('/case-studies') ? 'text-teal-300' : 'text-white/90'
                }`}
            >
              Case Studies
            </Link>
            <Link
              to="/about"
              className={`text-sm font-bold transition-colors hover:text-teal-300 ${isActive('/about') ? 'text-teal-300' : 'text-white/90'
                }`}
            >
              About
            </Link>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-teal-400 text-[#4C3362] font-bold px-6 py-3 rounded-xl hover:bg-teal-300 hover:shadow-xl hover:shadow-teal-400/50 transition-all text-sm">
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-white/90 hover:text-white transition-colors"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden backdrop-blur-lg border-t border-white/10" style={{
          background: 'rgba(76, 51, 98, 0.95)'
        }}>
          <div className="container py-4">
            <nav className="flex flex-col gap-1">
              <Link to="/" className="px-4 py-3 rounded-xl hover:bg-white/10 transition-colors font-bold text-white/90" onClick={() => setMobileMenuOpen(false)}>
                Home
              </Link>

              {/* Services with submenu */}
              <div>
                <button
                  onClick={() => setServicesOpen(!servicesOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl hover:bg-white/10 transition-colors font-bold text-white/90"
                >
                  Services
                  <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {servicesOpen && (
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-teal-400/50 pl-2">
                    <Link
                      to="/services"
                      className="block px-4 py-2 rounded-lg hover:bg-white/10 transition-colors text-sm font-semibold text-teal-300"
                      onClick={() => { setServicesOpen(false); setMobileMenuOpen(false); }}
                    >
                      View All Services
                    </Link>
                    {SERVICES.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => {
                          handleServiceClick(service.slug);
                        }}
                        className="w-full text-left px-4 py-2 rounded-lg hover:bg-white/10 transition-colors text-sm font-medium text-white/80"
                      >
                        {service.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Link to="/case-studies" className="px-4 py-3 rounded-xl hover:bg-white/10 transition-colors font-bold text-white/90" onClick={() => setMobileMenuOpen(false)}>
                Case Studies
              </Link>
              <Link to="/about" className="px-4 py-3 rounded-xl hover:bg-white/10 transition-colors font-bold text-white/90" onClick={() => setMobileMenuOpen(false)}>
                About
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-teal-400 text-[#4C3362] font-bold px-6 py-3 rounded-xl hover:bg-teal-300 transition-all mt-4 justify-center" onClick={() => setMobileMenuOpen(false)}>
                Get Started
              </Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;